import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  StatusBadge,
  Separator,
} from '@databro/ui';

export default function Dashboard() {
  const { data: actions } = useQuery({
    queryKey: ['actions'],
    queryFn: () => api.getActions(),
  });

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.getClients(),
  });

  const { data: executions } = useQuery({
    queryKey: ['executions', { limit: 10 }],
    queryFn: () => api.getExecutions({ limit: 10 }),
  });

  const onlineClients = clients?.filter((c) => c.isOnline).length ?? 0;
  const totalClients = clients?.length ?? 0;

  const recentSuccess = executions?.data.filter((e) => e.status === 'success').length ?? 0;
  const recentFailed = executions?.data.filter((e) => e.status === 'failed').length ?? 0;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your DataBro instance</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold">{actions?.length ?? 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Clients Online
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold">
              {onlineClients}{' '}
              <span className="text-lg text-muted-foreground">/ {totalClients}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Recent Success
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold text-green-500">{recentSuccess}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Recent Failed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold text-destructive">{recentFailed}</div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Executions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Recent Executions</CardTitle>
          <Button variant="link" size="sm" asChild>
            <Link to="/executions">View all</Link>
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {executions?.data.length === 0 && (
            <div className="px-6 py-8 text-center text-muted-foreground">
              No executions yet
            </div>
          )}
          {executions?.data.map((execution, index) => (
            <div key={execution.id}>
              {index > 0 && <Separator />}
              <div className="px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="font-medium">{execution.action.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {new Date(execution.createdAt).toLocaleString()}
                  </div>
                </div>
                <StatusBadge status={execution.status} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Clients */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Clients</CardTitle>
          <Button variant="link" size="sm" asChild>
            <Link to="/clients">View all</Link>
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {clients?.length === 0 && (
            <div className="px-6 py-8 text-center text-muted-foreground">
              No clients connected yet. Install the Chrome extension to get started.
            </div>
          )}
          {clients?.slice(0, 5).map((client, index) => (
            <div key={client.id}>
              {index > 0 && <Separator />}
              <div className="px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="font-medium">
                    {client.name || client.id.slice(0, 8)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {client.actionCount} actions
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${
                      client.isOnline ? 'bg-green-500' : 'bg-muted-foreground/30'
                    }`}
                  />
                  <span className="text-sm text-muted-foreground">
                    {client.isOnline ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
